/**
 * Classe permettant de gérer la bascule entre plusieurs variantes d'un même composant selon des règles définies.
 */
export default class Switcher {

    /**
     * Créer un switcher
     *
     * @param {string} firstId Identifiant de la variante
     * @param {Object} firstComponent options de la variante
     *
     * Liste des options :
     * - element = élément du DOM à récupérer (peut être un <template>, ou une fonction, asynchrone ou non, qui renvoie un élément de DOM en valeur de retour)
     * - onMounted = méthode appelée lorsque la variante est ajoutée au DOM
     * - onUnmounted = méthode appelée lorsque la variante est retirée du DOM
     * - loadingComponentId = identifiant du composant utilisé pendant le chargement du composant si celui-ci est asynchrone. Le composant de chargement doit être déclaré au préalable et ne pas être asynchrone.
     * - errorComponentId = identifiant du composant utilisé en cas d'erreur du chargement du composant si celui-ci est asynchrone. Le composant d'erreur doit être déclaré au préalable et ne pas être asynchrone.
     */
    constructor(firstId, firstComponent) {
        if (this.isAsyncElement(firstComponent.element)) {
            throw new Error('First component CANNOT be a function, it has to be present in the page when the switcher is initialized.');
        }
        this.registeredComponents = new Map();
        this.currentId = firstId; // ID du composant qui a été requis
        this.actualId = firstId; // ID du composant effectivement affiché
        const element = this.registerComponent(firstId, firstComponent);
        this.replaceElement(firstComponent.element, element);
        this.mountComponent();
        this.lastUpdate = Date.now();
    }

    /**
     * Ajoute une variante
     * @param {string} id Identifiant de la variante
     * @param {Object} componentOptions options de la variante
     * @return Élément du DOM qui sera utilisé, ou null si c'est une fonction
     * @throws {Error}
     *
     * Liste des options :
     * - element = élément du DOM à récupérer (peut être un <template>, ou une fonction, asynchrone ou non, qui renvoie un élément de DOM en valeur de retour)
     * - onMounted = méthode appelée lorsque la variante est ajoutée au DOM
     * - onUnmounted = méthode appelée lorsque la variante est retirée du DOM
     */
    registerComponent(id, componentOptions) {
        if (this.isDOMElement(componentOptions.element)) {
            this._registerDOMElementComponent(id, componentOptions);
        } else if (this.isAsyncElement(componentOptions.element)) {
            this._registerAsyncElementComponent(id, componentOptions);
        } else {
            throw new Error(`Cannot register component: unknown component type for ${id}.`);
        }
        this.registeredComponents.set(id, componentOptions);
        return componentOptions._actualElement;
    }

    /**
     * (Privé) Enregistre un composant de type DOM
     * @param {string} id Identifiant de la variante
     * @param {Object} componentOptions options de la variante
     * @private
     */
    _registerDOMElementComponent(id, componentOptions) {
        componentOptions._actualElement = this.cloneElement(componentOptions.element);
    }

    /**
     * (Privé) Enregistre un composant de type Asynchrone
     * @param {string} id Identifiant de la variable
     * @param {Object} componentOptions options de la variante
     * @private
     * @throws {Error}
     */
    _registerAsyncElementComponent(id, componentOptions) {
        componentOptions._actualElement = null;
        if (componentOptions.errorComponentId) {
            this._ensureDOMComponent(componentOptions.errorComponentId, {
                componentNotFoundErrorMessage: id => `Error component does not exist: ${id}`,
                asyncComponentErrorMessage: () => 'Error component CANNOT be a function, it has to be present in the page when the switch occurs.'
            });
        }
        if (componentOptions.loadingComponentId) {
            this._ensureDOMComponent(componentOptions.loadingComponentId, {
                componentNotFoundErrorMessage: id => `Loading component does not exist: ${id}`,
                asyncComponentErrorMessage: () => 'Loading component CANNOT be a function, it has to be present in the page when the switch occurs.'
            });
        }
    }

    /**
     * (Privé) S'assure que le composant fourni est bien un composant de type DOM
     * @param {string} id Identifiant du composant
     * @param {Object} messages Messages à utiliser en cas d'erreur
     * @private
     * @throws {Error}
     */
    _ensureDOMComponent(id, messages) {
        if (!(this.registeredComponents.has(id))) {
            throw new Error(messages.componentNotFoundErrorMessage(id));
        }
        if (this.isAsyncElement(this.registeredComponents.get(id))) {
            throw new Error(messages.asyncComponentErrorMessage(id));
        }
    }

    /**
     * Remplace le composant d'une variante
     * @param {string} id Identifiant de la variante
     * @param {Object} componentOptions options de la variante
     *
     * Liste des options :
     * - element = élément du DOM à récupérer (peut être un <template>, ou une fonction, asynchrone ou non, qui renvoie un élément de DOM en valeur de retour)
     * - onMounted = méthode appelée lorsque la variante est ajoutée au DOM
     * - onUnmounted = méthode appelée lorsque la variante est retirée du DOM
     */
    replaceComponent(id, componentOptions) {
        const currentElement = this.unregisterComponent(id);
        const element = this.registerComponent(id, componentOptions);
        this.replaceElement(currentElement, element);
    }

    /**
     * Supprime une variante
     * @param {string} id ID de la variante à retirer
     * @return {HTMLElement} Élément du DOM qui doit être remplacé ou null si ce n'était pas le composant en cours d'affichage
     */
    unregisterComponent(id) {
        const element = this.currentId === id ? this.unmountComponent() : null;
        this.registeredComponents.delete(id);
        return element;
    }

    /**
     * Indique si l'élément fourni est un élément du DOM
     * @param {HTMLElement} element Élément à tester
     * @return {boolean} true si c'est un élément du DOM, false sinon.
     */
    isDOMElement(element) {
        return element instanceof HTMLElement;
    }

    /**
     * Indique si l'élément fourni est un élément du DOM de type <template>
     * @param {HTMLElement} element Élément à tester
     * @return {boolean} true si c'est un template, false sinon.
     */
    isTemplateElement(element) {
        return element instanceof HTMLTemplateElement;
    }

    /**
     * Indique si un composant utilise une fonction qui retournera un élément
     * @param {HTMLElement} element Élément à tester
     * @return {boolean} true si c'est un élément du DOM, false sinon.
     */
    isAsyncElement(element) {
        return element instanceof Function;
    }

    /**
     * Clone un élément du DOM
     * @param {HTMLElement} element Élément à cloner
     * @return {HTMLElement} Clone de l'élément
     */
    cloneElement(element) {
        if (this.isTemplateElement(element)) {
            const fragment = element.content.cloneNode(true);
            const wrapper = document.createElement('div');
            wrapper.appendChild(fragment);
            const [ child ] = wrapper.children;
            return child;
        }
        return element.cloneNode(true);
    }

    /**
     * Appelle la fonction de montage d'un composant
     * @param {Object} payload Objet optionnel à envoyer à la fonction de montage du composant
     */
    mountComponent(payload = { }) {
        const currentComponent = this.registeredComponents.get(this.actualId);
        currentComponent?.onMounted?.(currentComponent?._actualElement, payload);
    }

    /**
     * Appelle la fonction de démontage d'un composant
     * @param {Object} payload Objet optionnel à envoyer à la fonction de démontage du composant
     * @return {HTMLElement} Renvoie l'élément qui doit être détruit ou remplacé
     */
    unmountComponent(payload = { }) {
        const currentComponent = this.registeredComponents.get(this.actualId);
        currentComponent?.onUnmounted?.(currentComponent?._actualElement, payload);
        return currentComponent?._actualElement;
    }

    /**
     * Remplace un élément du DOM par un autre
     * @param {HTMLElement} oldElement ancien élément à remplacer
     * @param {HTMLElement} newElement nouvel élément qui vient se loger à la place de l'ancien
     */
    replaceElement(oldElement, newElement) {
        oldElement.replaceWith(newElement);
    }

    /**
     * Retourne une copie de l'élément destiné à être placé dans le DOM
     * @param {string} id Identifiant du composant
     * @return {Promise<{id: string, error: Error|null, element: HTMLElement}>} Élément du DOM cloné, Erreur si présente, et identifiant final du composant utilisé
     */
    async retrieveElementFor(id) {
        let element, error = null;
        const component = this.registeredComponents.get(id);
        if (!component) throw new Error('Unknow component identifier: ' + id);
        if (this.isAsyncElement(component.element)) {
            const response = await this._retrieveElementForAsyncComponent(id, component);
            element = response.element;
            error = response.error;
            id = response.id;
        } else {
            element = component._actualElement;
        }
        element = this.cloneElement(element);
        if (error) console.error(error);
        return { element, error, id };
    }

    /**
     * (Privé) Retourne une copie de l'élément destiné à être placé dans le DOM, méthode spécifique pour un composant asynchrone.
     * @param {string} id Identifiant du composant
     * @param component Composant à utiliser
     * @return {Promise<{id: string, error: Error|null, element: HTMLElement}>} Élément du DOM cloné, Erreur si présente, et identifiant final du composant utilisé
     * @private
     */
    async _retrieveElementForAsyncComponent(id, component) {
        let element, error = null;
        try {
            if (component.loadingComponentId) {
                await this.switch(component.loadingComponentId, true);
            }
            element = await component.element();
        } catch (e) {
            error = e;
            id = component.errorComponentId;
            const response = await this.retrieveElementFor(component.errorComponentId);
            element = response.element;
        }
        return { element, error, id };
    }

    /**
     * Effectue la bascule entre l'ancienne et la nouvelle variante
     * @param {string} id Identifiant de la nouvelle variante de composant à utiliser
     * @param {boolean} force Force l'utilisation du composant, même si d'autres composants ont été appelés en asynchrone et ne sont pas encore résolus
     * @return {Promise} Fonction asynchrone
     */
    async switch(id, force = false) {
        const localTimestamp = Date.now();
        if (!id) throw new Error('ID cannot be null');
        if (id === this.currentId) return;
        if (!force) {
            this.currentId = id;
            this.lastUpdate = localTimestamp;
        }
        const { element, error = null, id: finalId } = await this.retrieveElementFor(id);
        this.registeredComponents.get(finalId)._actualElement = element;
        if (!force) {
            if (this.lastUpdate > localTimestamp) return // C'est toujours le dernier appel qui a raison : si une promesse a mis trop de temps à se résoudre, tant pis pour elle.
        }
        const currentElement = this.unmountComponent();
        this.replaceElement(currentElement, element);
        this.actualId = finalId;
        this.mountComponent({ error });
    }

    /**
     * Indique quel est le composant qui est théoriquement affiché (demandé explicitement)
     * @return {string} Identifiant du composant
     */
    get currentComponentId() {
        return this.currentId;
    }

    /**
     * Indique quel est le composant qui est, en pratique, affiché (peut être un composant de chargement ou d'erreur)
     * @return {string} Identifiant du composant
     */
    get actualComponentId() {
        return this.actualId;
    }
}